import { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import SectionHeading, { SectionBadge } from '../../components/SectionHeading'

export type ServiceItem = {
  title: string
  description: string
  bgImage: string
  to?: string
  action?: 'transfers'
}

const services: ServiceItem[] = [
  {
    title: 'Day Trips',
    description:
      'Our Salzburg Cab Service provide Private Day Trip options. Offering flexible way to see more.',
    bgImage: '/assets/Card cover/Day.svg',
    to: '/day-trips',
  },
  {
    title: 'Private Europe Tours',
    description:
      'Explore Europe in comfort with private mini van tours designed around your schedule.',
    bgImage: '/assets/Card cover/Private.svg',
    to: '/private-tours',
  },
  {
    title: 'Ski Transfer',
    description:
      'Explore the best ski destinations with comfortable private transfers from Salzburg.',
    bgImage: '/assets/Card cover/Ski.svg',
    to: '/ski-transfers',
  },
  {
    title: 'Airport Transfer',
    description:
      'We will bring you quickly and comfortably to anywhere in your city with our Salzburg Cab Service',
    bgImage: '/assets/Card cover/Airport.svg',
    action: 'transfers',
  },
  {
    title: 'Hotel Transfer',
    description:
      'Comfortable transfers to airports, hotels, and top destinations from Salzburg. Book your ride today.',
    bgImage: '/assets/Card cover/hotel.svg',
    action: 'transfers',
  },
]

type ServiceCardProps = ServiceItem

function ServiceCard({
  title,
  description,
  bgImage,
  to,
  action,
}: ServiceCardProps) {
  const navigate = useNavigate()

  const triggerAction = (isNewTab = false) => {
    if (action === 'transfers') {
      window.dispatchEvent(
        new CustomEvent('salzburg-set-hero-tab', { detail: 'transfers' })
      )
      const el = document.getElementById('transfers')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    } else if (to) {
      if (isNewTab) {
        window.open(to, '_blank')
      } else {
        navigate(to)
      }
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    const isNewTab = e.metaKey || e.ctrlKey
    if (!isNewTab && action === 'transfers') {
      e.preventDefault()
    }
    triggerAction(isNewTab)
  }

  return (
    <motion.article
      onClick={handleClick}
      role="link"
      tabIndex={0}
      aria-label={`${title} - ${action === 'transfers' ? 'Book transfer on hero section' : 'Go to ' + title}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          triggerAction(false)
        }
      }}
      variants={{
        hidden: {
          opacity: 0,
          y: 30,
        },

        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.6,
            ease: 'easeOut',
          },
        },
      }}
      className="
        group
        relative
        aspect-[257/204]

        w-[calc(100vw-48px)]
        min-w-[calc(100vw-48px)]

        max-w-[320px]

        shrink-0
        snap-start

        cursor-pointer
        overflow-hidden
        rounded-[20px]

        shadow-[0_5px_7px_rgba(0,0,0,0.16)]

        transition-all
        duration-300

        hover:-translate-y-1
        hover:shadow-lg

        sm:w-[305px]
        sm:min-w-[305px]

        lg:h-[204px]
        lg:w-full
        lg:min-w-0
        lg:max-w-none
        lg:aspect-auto
      "
    >
      {/* CARD DESIGN / SVG */}
      <img
        src={bgImage}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="
          pointer-events-none
          absolute
          inset-0

          block
          h-full
          w-full

          select-none
          object-fill

          transition-transform
          duration-500

          group-hover:scale-[1.015]
        "
      />

      {/* CARD CONTENT */}
      <div
        className="
          pointer-events-none
          relative
          z-10

          flex
          h-full
          w-full
          flex-col

          px-[9.5%]
          pt-[39%]
          pb-[8%]

          lg:px-5
          lg:pt-20
          lg:pb-5
        "
      >
        <div
          className="
            w-[76%]
            max-w-[220px]

            lg:w-auto
            lg:max-w-[180px]
          "
        >
          <h3
            className="
              font-host

              text-[17px]
              font-medium
              leading-[1.2]
              tracking-[-0.03em]

              text-white

              transition-colors
              duration-200

              group-hover:text-[#FFB08C]

              lg:text-[16px]
            "
          >
            {title}
          </h3>

          <p
            className="
              mt-[9px]

              font-host

              text-[14px]
              font-normal
              leading-[1.45]
              tracking-[-0.03em]

              text-white

              lg:mt-2
              lg:text-[14px]
            "
          >
            {description}
          </p>
        </div>
      </div>
    </motion.article>
  )
}

export default function Services() {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>

    const handleScroll = () => {
      if (window.innerWidth >= 768) return
      
      const el = scrollRef.current
      if (!el) return

      interval = setInterval(() => {
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 10) {
          el.scrollTo({ left: 0, behavior: 'smooth' })
        } else {
          el.scrollBy({ left: 320, behavior: 'smooth' })
        }
      }, 3000)
    }

    handleScroll()
    window.addEventListener('resize', () => {
      clearInterval(interval)
      handleScroll()
    })

    return () => clearInterval(interval)
  }, [])

  return (
    <section
      id="services"
      className="
        mx-auto
        w-full
        max-w-page

        overflow-hidden

        px-4
        py-16

        lg:px-11
        lg:py-20
      "
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          margin: '-100px',
        }}
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
        className="flex flex-col gap-3"
      >
        {/* SECTION HEADING */}
        <motion.div
          variants={{
            hidden: {
              opacity: 0,
              y: 20,
            },

            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                ease: 'easeOut',
              },
            },
          }}
          className="flex flex-col gap-3"
        >
          <SectionBadge className="w-fit self-start px-4">
            Our Services
          </SectionBadge>

          <SectionHeading>
            Travel Services We Offer
          </SectionHeading>
        </motion.div>

        {/* SERVICE CARDS */}
        <div
          ref={scrollRef}
          className="
            -mr-4
            mt-3

            flex
            w-[calc(100%+16px)]

            gap-4

            overflow-x-auto
            overflow-y-hidden

            scroll-smooth

            snap-x
            snap-mandatory

            pb-5
            pr-4

            scrollbar-hide

            lg:mr-0
            lg:grid
            lg:w-full
            lg:grid-cols-5
            lg:gap-4
            lg:overflow-visible
            lg:pb-0
            lg:pr-0
          "
        >
          {services.map((service) => (
            <ServiceCard
              key={service.title}
              {...service}
            />
          ))}
        </div>
      </motion.div>
    </section>
  )
}